// components/.../BrowserPreview.jsx
import React, { useRef, useState, useEffect } from 'react';

const BrowserPreview = ({
  pageSlug,
  title,
  description,
  audioUrl,
  audioFileName,
  media = [],
  revealDate,
  revealTime,
  hasContent
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  /* Reset playback state whenever the source changes */
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [audioUrl]);

  /* ── Format seconds → MM:SS ── */
  const formatTime = (s) => {
    if (!isFinite(s) || s < 0) return '00:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m.toString().padStart(2, '0')}:${sec
      .toString()
      .padStart(2, '0')}`;
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch((err) => console.error('Play failed:', err));
    } else {
      el.pause();
    }
  };

  const handleSeek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width)
    );
    el.currentTime = ratio * duration;
  };

  /* ── Blank state ── */
  if (!hasContent) {
    return (
      <div className="bg-white p-8 min-h-[600px] flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F5F5F5] flex items-center justify-center">
            <svg
              className="w-8 h-8 text-[#B0B0B0]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
            >
              <rect x="4" y="4" width="16" height="16" rx="2" />
              <path d="M4 10h16" />
            </svg>
          </div>
          <p className="text-[#6B635D] text-base font-normal">
            Your memory preview will appear here
          </p>
          <p className="text-sm text-[#979797] mt-1">
            Start by typing a title on the left
          </p>
        </div>
      </div>
    );
  }

  const progressRatio = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="bg-white min-h-[600px] pt-32 flex flex-col items-center justify-center">
      {/* ── Media slider — above title/description ── */}
      {media.length > 0 && (
        <div className="w-full max-w-2xl mb-8">
          <div className="flex gap-1 overflow-x-auto hide-scrollbar pb-3 snap-x snap-mandatory">
            {media.map((item, i) => (
              <div
                key={i}
                className="relative flex-shrink-0 w-[299px] h-[376px] snap-start overflow-hidden  "
              >
                {item.type === 'video' ? (
                  <video
                    src={item.url}
                    className="w-[299px] h-full object-cover"
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.name || `Media ${i + 1}`}
                    className="w-[299px] h-full object-cover"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Title ── */}
      {title && (
        <div className="text-center mb-1">
          <h3 className="text-[32px] font-bold text-[#1C2A46] mb-4 tracking-tight text-center">
            {title}
          </h3>
        </div>
      )}

      {/* ── Description + audio ── */}
      {(description || audioUrl) && (
        <div className="max-w-2xl w-full mb-10">
          <div>
            {description && (
              <p className="text-[#6B635D] text-sm font-light mb-4 whitespace-pre-wrap">
                {description}
              </p>
            )}

            {/* Real audio element (hidden) */}
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
              />
            )}

            {/* Audio player UI */}
            {audioUrl && (
              <div className="mt-6 p-8  ">
                <div className="flex items-center gap-4">
                  {/* Play / pause */}
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="w-12 h-12 rounded-full bg-[#1C2A46] flex items-center justify-center flex-shrink-0 hover:bg-[#2a3a5c] transition-colors"
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? (
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 text-white ml-0.5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>

                  {/* Time */}
                  <div className="text-base text-[#1C2A46] font-mono flex-shrink-0 tabular-nums">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>

                  {/* Waveform — click to seek */}
                  <div
                    className="flex-1 flex items-center gap-[2px] h-12 cursor-pointer select-none"
                    onClick={handleSeek}
                    role="slider"
                    aria-label="Audio position"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progressRatio * 100)}
                  >
                    {Array.from({ length: 48 }).map((_, i) => {
                      const barRatio = i / 48;
                      const isPlayed = barRatio <= progressRatio;
                      const barHeight = 6 + ((i * 7) % 34);
                      return (
                        <div
                          key={i}
                          className={`w-[2px] rounded-full ${
                            isPlayed ? 'bg-[#55000C]' : 'bg-[#B0A8A0]'
                          }`}
                          style={{ height: `${barHeight}px` }}
                        />
                      );
                    })}
                  </div>

                  {/* Playhead dot */}
                  <div className="relative w-2 h-2 rounded-full bg-[#55000C] flex-shrink-0 pointer-events-none" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowserPreview;